import React, { Component, createRef } from "react";
import PropTypes from "prop-types";
import ProgressButton from "react-progress-button";
import MaskedInput from "react-text-mask";
import classNames from "classnames";
import { withLDConsumer } from "launchdarkly-react-client-sdk";

import utils from "@utils";
import { StateSelection } from "@components/common";
import { JOB_INVOICED_TYPES, MASK_VALUE } from "@constants";
import { ClientService, ContactService, OrganizationService } from "@services";
import { Modal, ConfirmationModal } from "../../../../../components";
import { ClientCoordinators, UploadFiles } from "../../../../clients";

import "./JobFormModals.scss";

class AddClientModal extends Component {
	constructor(props) {
		super(props);

		const {
			flags: { newClientBillingInfo },
		} = props;

		this.newClientBillingInfoEnabled = !!newClientBillingInfo;

		this.getAgencyRules();
		this.getPaymentRules();
		this.state = {
			isAgencyClient: false,
			buttonState: "",
			contactState: {},
			clientState: {},
			Name: "",
			FirstName: "",
			LastName: "",
			EmailAddress: "",
			MobilePhone: "",
			StreetAddress1: "",
			City: "",
			StateId: "",
			JobInvoicedTypeId: null,
			Zipcode: "",
			ClientCoordinatorIds: [],
			clientDocuments: [],
			AuthorizedToUseDepartmentVehicles: false,
			AllowExcludeOfficerRate: false,
			TransferOfficerPaymentsToAgency: false,
			agencyRules: {
				OnlyAuthorizedClientsAreAllowedToUseVehicles: false,
				RequireInvoicingForVehicles: false,
			},
			agencyPaymentRules: {
				TransferPaymentToJurisdiction: false,
				TransferPaymentsToAgencyOption: false,
			},
			showAllowExcludeOfficerRate: false,
		};

		this._confirmationModal = createRef();
	}

	componentDidMount() {
		const { user } = this.props;
		// FE-1082: The Agency Client checkbox should only appear for LEAs who are staff admin or internal coordinator
		// The box should be checked by default for staff admins and set to true for LEA staff members (cannot see the checkbox)
		// Clients for all other user types should always be created as an agency level client no matter what
		if ((user.isLEA() && !user.IsInternalCoordinator) || !user.isLEA()) {
			this.setState({ isAgencyClient: true });
		}
	}

	getAgencyRules = () => {
		const { lang } = global;
		const { user } = this.props;

		if (user.isLEA()) {
			const promises = [OrganizationService.getJurisdictionAgencyRules()];
			Promise.all(promises)
				.then(([{ data: agencyRules }]) => {
					this.setState(() => ({
						agencyRules,
					}));
				})
				.catch(() =>
					utils.notify({
						type: "error",
						message: lang.t("Error getting Jurisdiction Agency Rules"),
						timeOut: 1500,
					}),
				);
		}
	};

	getPaymentRules = () => {
		const { lang } = global;
		const { user } = this.props;

		if (user.isLEA()) {
			const promises = [OrganizationService.getJurisdictionPaymentRules()];
			Promise.all(promises)
				.then(([{ data: agencyPaymentRules }]) => {
					this.setState(() => ({
						agencyPaymentRules,
					}));
				})
				.catch(() =>
					utils.notify({
						type: "error",
						message: lang.t("Error getting Jurisdiction Agency Rules"),
						timeOut: 1500,
					}),
				);
		}
	};

	onEntered = () => {
		const input = document.getElementById("newClientName");
		input.focus();
	};

	isValid = () => {
		const { Name } = this.state;
		const clientNameError = Name.trim() === "";

		if (clientNameError) {
			this.setState({ clientState: { clientNameError } });
		}

		return !clientNameError;
	};

	isValidBillingContactInformation = () => {
		const { FirstName, LastName, EmailAddress, MobilePhone } = this.state;

		const firstNameError = FirstName === "";
		const lastNameError = LastName === "";
		const phoneError = MobilePhone === "" || utils.revertPhoneMask(MobilePhone).length !== 10;
		const emailError = !/^\S+@\S+\.\S{2,}$/.test(EmailAddress);

		const contactState = {
			firstNameError,
			lastNameError,
			phoneError,
			emailError,
		};

		const isAllEmpty = firstNameError && lastNameError && phoneError && emailError;

		if (!isAllEmpty) {
			this.setState({ contactState });
		}

		return isAllEmpty || (!firstNameError && !lastNameError && !phoneError && !emailError);
	};

	onExited = () => {
		const { user } = this.props;
		this.setState({
			isAgencyClient: (user.isLEA() && !user.IsInternalCoordinator) || !user.isLEA(),
			Name: "",
			FirstName: "",
			LastName: "",
			EmailAddress: "",
			MobilePhone: "",
			StreetAddress1: "",
			City: "",
			StateId: "",
			Zipcode: "",
			ClientCoordinatorIds: [],
			clientDocuments: [],
			buttonState: "",
			contactState: {},
			clientState: {},
			JobInvoicedTypeId: null,
		});
	};

	performMergeContact = async (contactId, clientId) => {
		const { lang } = global;
		const params = {
			Id: contactId,
			clientId,
		};

		try {
			await ContactService.mergeContact(params);

			utils.notify({
				type: "success",
				message: lang.t("Contact successfully merged"),
				timeOut: 1500,
			});

			return true;
		} catch {
			utils.notify({
				type: "error",
				message: lang.t("Unable to merge contact"),
				timeOut: 1500,
			});

			return false;
		}
	};

	onSubmit = async event => {
		event?.preventDefault();

		const { lang } = global;
		const { user, onSave } = this.props;
		const {
			Name,
			FirstName,
			LastName,
			EmailAddress,
			MobilePhone,
			StreetAddress1,
			City,
			StateId,
			Zipcode,
			ClientCoordinatorIds,
			clientDocuments,
			isAgencyClient,
			AuthorizedToUseDepartmentVehicles,
			AllowExcludeOfficerRate,
			TransferOfficerPaymentsToAgency,
			JobInvoicedTypeId,
		} = this.state;

		if (!this.isValid() || !this.isValidBillingContactInformation()) return;

		this.setState({ buttonState: "loading" });
		const request = {
			Name,
			AuthorizedToUseDepartmentVehicles,
			AllowExcludeOfficerRate,
			TransferOfficerPaymentsToAgency,
			ClientCoordinatorIds: isAgencyClient
				? []
				: ClientCoordinatorIds.map(({ value }) => value),
			CloudFileIds: clientDocuments.map(file => file.CloudFileId),
			IsActive: true,
			JobInvoicedTypeId,
		};

		const onSuccess = () => {
			utils.notify({
				type: "success",
				message: `${lang.t("Client successfully")} ${lang.t("Created")}`,
				timeOut: 1500,
			});

			this.setState({ buttonState: "success" }, () => {
				setTimeout(() => {
					onSave();
				}, 500);
			});
		};

		try {
			if (EmailAddress) {
				const billingContact = {
					FirstName,
					LastName,
					EmailAddress,
					StreetAddress1,
					MobilePhone,
					City,
					StateId,
					Zipcode,
				};

				const contactId = await ContactService.getContactByEmail(EmailAddress)
					.then(
						({ data: { Id } }) => Id,
						() => false,
					)
					.catch(() => false);

				if (contactId) {
					this._confirmationModal.openModal({
						title: "Merge Contact",
						message: lang.t(
							"A contact with this email already exists_ Do you want to associate the client with this existing contact_",
						),
						confirmButton: "Yes",
						onConfirm: async () => {
							request.BillingContactId = contactId;
							const {
								data: { Id: clientId },
							} = await ClientService.save(
								request,
								user.isCoordinator() ? { createDefaultProject: true } : undefined,
							);
							if (await this.performMergeContact(contactId, clientId)) {
								onSuccess();
							}
						},
						onCancel: () => this.setState({ buttonState: "error" }),
					});
				} else {
					const { data: newClient } = await ClientService.save(
						request,
						user.isCoordinator() ? { createDefaultProject: true } : undefined,
					);
					billingContact.clientId = newClient.Id;

					const { data: newContact } = await ContactService.createContact(billingContact);
					newClient.BillingContactId = newContact.Id;

					await ClientService.save(newClient);
					onSuccess();
				}
			} else {
				await ClientService.save(
					request,
					user.isCoordinator() ? { createDefaultProject: true } : undefined,
				);
				onSuccess();
			}
		} catch {
			this.setState({ buttonState: "error" });
			utils.notify({
				type: "error",
				message: lang.t("Error creating client"),
				timeOut: 1500,
			});
		}
	};

	toggleAgencyClient = () =>
		this.setState(prevState => ({ isAgencyClient: !prevState.isAgencyClient }));

	handleAuthorizedToUseDepartmentVehicles = () => {
		this.setState(prevState => ({
			AuthorizedToUseDepartmentVehicles: !prevState.AuthorizedToUseDepartmentVehicles,
			AllowExcludeOfficerRate: false,
			showAllowExcludeOfficerRate: !prevState.showAllowExcludeOfficerRate,
		}));
	};

	render() {
		const { lang } = global;
		const {
			TransferOfficerPaymentsToAgency,
			AuthorizedToUseDepartmentVehicles,
			AllowExcludeOfficerRate,
			Name,
			FirstName,
			LastName,
			EmailAddress,
			MobilePhone,
			StreetAddress1,
			City,
			StateId,
			Zipcode,
			ClientCoordinatorIds,
			clientDocuments,
			isAgencyClient,
			JobInvoicedTypeId,
			buttonState,
			agencyRules,
			agencyPaymentRules,
			contactState: {
				firstNameError = false,
				lastNameError = false,
				phoneError = false,
				emailError = false,
			},
			clientState: { clientNameError = false },
			showAllowExcludeOfficerRate,
		} = this.state;
		const { user, show, onCancel } = this.props;

		const isInvoicingV3 = user.Features.some(f => f.toLowerCase() === "invoicingv3");

		return (
			<Modal
				id="add-client-modal"
				show={show}
				onEntered={this.onEntered}
				onHide={onCancel}
				onExited={this.onExited}
				size="lg"
			>
				<Modal.Header closeButton>
					<Modal.Title>
						{lang.t("New")} {lang.t("Client")}
					</Modal.Title>
				</Modal.Header>
				<Modal.Body>
					<div className="row">
						<div className="col-sm-12 form-group">
							<label
								htmlFor="newClientName"
								className="control-label"
							>
								{lang.t("Client Name")}
							</label>
							<input
								type="text"
								className={classNames("form-control", {
									error: clientNameError,
								})}
								id="newClientName"
								data-test-id="client__modal-name"
								name="newClientName"
								value={Name}
								maxLength="256"
								onChange={({ target: { value } }) => this.setState({ Name: value })}
							/>
						</div>
					</div>

					<If condition={this.newClientBillingInfoEnabled}>
						<strong className="text-left">Billing Contact Information</strong>

						<div className="row margin-top-15">
							<div className="col-md-6 form-group">
								<label
									htmlFor="newClientFirstName"
									className="control-label"
								>
									{lang.t("First Name")}
								</label>
								<input
									type="text"
									className={classNames("form-control", {
										error: firstNameError,
									})}
									name="newClientFirstName"
									data-test-id="client__modal-first-name"
									value={FirstName}
									onChange={({ target: { value } }) =>
										this.setState({ FirstName: value })
									}
								/>
							</div>

							<div className="col-md-6 form-group">
								<label
									htmlFor="newClientLastName"
									className="control-label"
								>
									{lang.t("Last Name")}
								</label>
								<input
									type="text"
									className={classNames("form-control", {
										error: lastNameError,
									})}
									name="newClientLastName"
									data-test-id="client__modal-last-name"
									value={LastName}
									onChange={({ target: { value } }) =>
										this.setState({ LastName: value })
									}
								/>
							</div>
						</div>

						<div className="row">
							<div className="col-sm-12 form-group">
								<label
									htmlFor="emailAddress"
									className="control-label"
								>
									{lang.t("Email Address")}:
								</label>
								<input
									name="EmailAddress"
									data-test-id="client__modal-email"
									type="email"
									className={classNames("form-control", {
										error: emailError,
									})}
									value={EmailAddress}
									onChange={({ target: { value } }) =>
										this.setState({ EmailAddress: value })
									}
								/>
							</div>
						</div>

						<div className="row">
							<div className="col-md-6 form-group">
								<label
									htmlFor="newClientPhoneNumber"
									className="control-label"
								>
									{lang.t("Phone Number")}
								</label>
								<MaskedInput
									name="MobilePhone"
									data-test-id="client__modal-mobile-phone"
									type="tel"
									mask={MASK_VALUE.phoneMask}
									className={classNames("form-control", {
										error: phoneError,
									})}
									value={MobilePhone}
									onChange={({ target: { value } }) =>
										this.setState({ MobilePhone: value })
									}
								/>
							</div>

							<div className="col-md-6 form-group">
								<label htmlFor="newClientStreetAddress">
									{lang.t("Street Address")}
								</label>
								<input
									type="text"
									className="form-control"
									name="newClientStreetAddress"
									data-test-id="client__modal-street-address"
									value={StreetAddress1}
									onChange={({ target: { value } }) =>
										this.setState({ StreetAddress1: value })
									}
								/>
							</div>
						</div>

						<div className="row">
							<div className="col-md-4 form-group">
								<label htmlFor="city">{lang.t("City")}:</label>
								<input
									name="City"
									data-test-id="client__modal-city"
									type="text"
									className="form-control"
									value={City}
									onChange={({ target: { value } }) =>
										this.setState({ City: value })
									}
								/>
							</div>

							<div className="col-md-4 form-group">
								<label htmlFor="state">{lang.t("State")}:</label>
								<StateSelection
									name="StateId"
									dataTestId="client__modal-state"
									value={StateId}
									onChange={({ target: { value } }) =>
										this.setState({ StateId: value })
									}
								/>
							</div>

							<div className="col-md-4 form-group">
								<label htmlFor="zipCode">{lang.t("Zip Code")}:</label>
								<input
									name="Zipcode"
									data-test-id="client__modal-zip-code"
									type="tel"
									className="form-control"
									maxLength={5}
									value={Zipcode}
									onChange={({ target: { value } }) =>
										this.setState({ Zipcode: value })
									}
								/>
							</div>
						</div>
					</If>
					<div className="row form-group">
						<div className="col-xs-12">
							<label htmlFor="JobInvoicedTypeId">{`${lang.t("Invoicing")}:`}</label>
						</div>
						<div className="col-xs-12 invoice-options-container">
							<div
								className="radio"
								data-role="Choosing the Officer option for this client will default this client to Officer based invoicing"
							>
								<label htmlFor="JobInvoicedTypeIdOfficer">
									<input
										type="radio"
										id="JobInvoicedTypeIdOfficer"
										name="JobInvoicedTypeIdOfficer"
										data-test-id="client__modal-officer-invoicing"
										checked={
											JobInvoicedTypeId ===
											JOB_INVOICED_TYPES.OFFICER_INVOICING
										}
										onClick={() =>
											this.setState({
												JobInvoicedTypeId:
													JOB_INVOICED_TYPES.OFFICER_INVOICING,
											})
										}
									/>
									{lang.t("Officer")}
								</label>
							</div>
							<If
								condition={
									user.Organization.NonRKPayInvoicingEnabled ||
									user.Organization.NonRKPayPaymentsEnabled ||
									isInvoicingV3
								}
							>
								<div
									className="radio"
									data-role="Choosing the Agency option for this client will default this client to Agency based invoicing"
								>
									<label htmlFor="JobInvoicedTypeIdAgency">
										<input
											type="radio"
											id="JobInvoicedTypeIdAgency"
											data-test-id="client__modal-agency-invoicing"
											name="JobInvoicedTypeIdAgency"
											checked={
												JobInvoicedTypeId ===
												JOB_INVOICED_TYPES.AGENCY_INVOICING
											}
											onClick={() =>
												this.setState({
													JobInvoicedTypeId:
														JOB_INVOICED_TYPES.AGENCY_INVOICING,
												})
											}
										/>
										{lang.t("Agency")}
									</label>
								</div>
							</If>
							<div
								className="radio"
								data-role="Choosing the Schedule only option for this client will default this client to Schedule only based jobs"
							>
								<label htmlFor="JobInvoicedTypeIdScheduleOnly">
									<input
										type="radio"
										id="JobInvoicedTypeIdScheduleOnly"
										data-test-id="client__modal-schedule-only-invoicing"
										name="JobInvoicedTypeIdScheduleOnly"
										checked={
											JobInvoicedTypeId === JOB_INVOICED_TYPES.SCHEDULE_ONLY
										}
										onClick={() =>
											this.setState({
												JobInvoicedTypeId: JOB_INVOICED_TYPES.SCHEDULE_ONLY,
											})
										}
									/>
									{lang.t("Scheduling Only")}
								</label>
							</div>
						</div>
					</div>

					<If
						condition={
							user.isLEA() && (user.IsStaffAdmin || user.IsInternalCoordinator)
						}
					>
						<div className="row">
							<div className="col-sm-12">
								<div className="checkbox">
									<label htmlFor="isAgencyClient">
										<input
											type="checkbox"
											id="isAgencyClient"
											data-test-id="client__modal-agency-client"
											name="isAgencyClient"
											onChange={() => this.toggleAgencyClient()}
											checked={isAgencyClient}
										/>
										{lang.t("Add as Agency Client")}
									</label>
								</div>
							</div>
						</div>
					</If>

					<If
						condition={
							this.newClientBillingInfoEnabled &&
							user.isLEA() &&
							user.IsStaffAdmin &&
							!isAgencyClient
						}
					>
						<hr />

						<div className="row">
							<label
								htmlFor="Coordinators"
								className="padding-left-15"
							>{`${lang.t("Coordinators")}:`}</label>

							<div className="col-sm-12">
								<ClientCoordinators
									user={user}
									ClientCoordinatorIds={ClientCoordinatorIds}
									onClientCoordinatorIdsChange={ids =>
										this.setState({ ClientCoordinatorIds: ids })
									}
								/>
							</div>
						</div>
					</If>

					<If
						condition={
							agencyRules.OnlyAuthorizedClientsAreAllowedToUseVehicles && user.isLEA()
						}
					>
						<hr />

						<div className="row">
							<label
								htmlFor="Permissions"
								className="padding-left-15"
							>{`${lang.t("Permissions")}:`}</label>

							<div className="col-sm-12">
								<div className="checkbox">
									<label htmlFor="client-authorized">
										<input
											type="checkbox"
											id="client-authorized"
											data-test-id="client__modal-authorized"
											name="client-authorized"
											onChange={this.handleAuthorizedToUseDepartmentVehicles}
											checked={AuthorizedToUseDepartmentVehicles}
										/>
										{lang.t("Client is authorized to use department vehicles")}
									</label>
								</div>
							</div>
							<If
								condition={
									agencyRules.RequireInvoicingForVehicles &&
									user.Organization.VehicleInvoicingEnabled &&
									showAllowExcludeOfficerRate
								}
							>
								<div className="col-sm-12">
									<div className="checkbox">
										<label htmlFor="client-exclude-officer-rate">
											<input
												type="checkbox"
												id="client-exclude-officer-rate"
												data-test-id="client__modal-exclude-officer-rate"
												name="exclude-officer-rate"
												onChange={({ target: { checked } }) =>
													this.setState({
														AllowExcludeOfficerRate: checked,
													})
												}
												checked={AllowExcludeOfficerRate}
											/>
											{lang.t(
												"Exclude officer pay for required vehicle invoicing",
											)}
										</label>
									</div>
								</div>
							</If>
						</div>
					</If>
					<If
						condition={
							agencyPaymentRules.TransferPaymentsToAgencyOption &&
							!agencyPaymentRules.TransferPaymentToJurisdiction
						}
					>
						<div className="row">
							<label
								htmlFor="Invoicing"
								className="padding-left-15"
							>{`${lang.t("Invoicing")}:`}</label>
							<div className="col-sm-12">
								<div className="checkbox">
									<label htmlFor="transfer-payments-agency">
										<input
											type="checkbox"
											id="transfer-payments-agency"
											data-test-id="client__modal-transfer-payments-agency"
											name="transfer-payments-agency"
											onChange={({ target: { checked } }) =>
												this.setState({
													TransferOfficerPaymentsToAgency: checked,
												})
											}
											checked={TransferOfficerPaymentsToAgency}
										/>
										{lang.t(
											"Transfer payments to agency for this client_s jobs",
										)}
									</label>
								</div>
							</div>
						</div>
					</If>

					<If condition={this.newClientBillingInfoEnabled}>
						<hr />

						<div className="row">
							<label
								htmlFor="Documents"
								className="padding-left-15"
							>{`${lang.t("Documents")}:`}</label>

							<div className="col-sm-12">
								<UploadFiles
									user={user}
									client={{ data: { Id: 0 } }}
									clientDocuments={clientDocuments}
									onClientDocumentsChange={documents =>
										this.setState({ clientDocuments: documents })
									}
									dataTestId="client__modal-upload-files"
								/>
							</div>
						</div>
					</If>
				</Modal.Body>
				<Modal.Footer>
					<button
						type="button"
						className="btn btn-link"
						onClick={onCancel}
						data-test-id="client__modal-cancel"
					>
						{lang.t("Cancel")}
					</button>
					<ProgressButton
						id="add-client"
						className="btn btn-primary"
						state={buttonState}
						onClick={event => this.onSubmit(event)}
						data-test-id="client__modal-add-client"
					>
						{lang.t("Save")}
					</ProgressButton>
				</Modal.Footer>

				<ConfirmationModal
					ref={ref => {
						this._confirmationModal = ref;
					}}
				/>
			</Modal>
		);
	}
}

AddClientModal.propTypes = {
	user: PropTypes.object.isRequired,
	show: PropTypes.bool,
	onCancel: PropTypes.func,
	onSave: PropTypes.func,
	flags: PropTypes.object.isRequired,
};

AddClientModal.defaultProps = {
	show: false,
	onCancel: () => {},
	onSave: () => {},
};

export default withLDConsumer()(AddClientModal);
